/**
 * @fileoverview Invites service: resolve invite token and accept invite.
 * @module features/orgs/api/invitesService
 */

import apiClient from '@/lib/apiClient'
import { type OrgId } from '../schemas/orgSchemas'
import { buildInviteLink } from '../utils/inviteLinks'

/**
 * Accept an invitation to join an organization.
 *
 * @param orgId Resolved target organization id
 * @param token Opaque invite token from the email link
 */
export async function acceptInvite(orgId: OrgId, token: string): Promise<void> {
  await apiClient.post(`/orgs/${orgId}/accept-invite`, { token })
}

/** Payload for inviting a user. */
export interface InviteMemberPayload {
  email: string
  role: 'OWNER' | 'ADMIN' | 'MEMBER'
}

/** Result for inviteMember: includes raw token and a ready-to-share link. */
export interface InviteMemberResult {
  token: string
  link: string
}

/**
 * Invite a user to an organization. Returns the invite token and a formatted link.
 * @param orgId Organization id
 * @param payload { email, role }
 * @returns { token, link }
 */
export async function inviteMember(
  orgId: OrgId,
  payload: InviteMemberPayload
): Promise<InviteMemberResult> {
  const orgIdStr = orgId as unknown as string
  const res = await apiClient.post(`/orgs/${orgIdStr}/invite`, payload)
  const token = String(res.data)
  return { token, link: buildInviteLink(orgId, token) }
}
